import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router';
import Immutable from 'immutable';
import { useSelector } from 'react-redux';
import { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import usePopup from 'hooks/usePopup';
import {
  ForumPostForm, Main, Popup, PopupCreateNickname,
} from '../../../components';
import { DEFAULT_LANGUAGE } from '../../../config/constants';
import useTypedDispatch from '../../../hooks/useTypedDispatch';

import { nodeAdd } from '../../../store/actions/Entity/Node';
import Page from '../../../containers/Page';
import useClasses from '../../../hooks/useClasses';
import useToast from '../../../hooks/useToast';


interface MatchParams {
  category: string;
}

const PageForumCreatePost: React.FC = () => {
  const dispatch = useTypedDispatch();
  const { buildClass } = useClasses('PageForumCreatePost Page');
  const { t } = useTranslation();
  const history = useHistory();
  const { category } = useParams<MatchParams>();
  const toast = useToast();


  const uid = useSelector<ImmutableRootState<number>, number>((state) => state.getIn(['login', 'uid'], null));
  const nickname = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['user', uid, 'field_nickname', 0, 'value'], ''));

  const popup = usePopup('createNicknamePopup', {
    closeOnEscape: false,
    closeOnOutsideClick: false,
  });

  useEffect(() => {
    if (!nickname) {
      popup.open();
    }
  }, [nickname, popup]);

  const handleSubmit = async (values) => {
    const forumPost = {
      type: [{ target_id: 'forum' }],
      title: [{ value: values.title }],
      body: [{ value: values.body }],
      taxonomy_forums: [{ target_id: category }],
    };

    try {
      // Wait for the node to be added.
      const response = await dispatch(nodeAdd(forumPost));
      const dataJson = await response.json();
      const data = Immutable.fromJS(dataJson);

      // Redirect to the detail page.
      const langcode = data.getIn(['langcode', 0, 'value'], DEFAULT_LANGUAGE);
      const path = data.getIn(['paths', langcode], '/');

      // Show success toast.
      toast.show(t('pages.forum_post_create.success_message'), 'success');

      history.push(path);
    } catch (error) {
      if (error) {
        toast.show(t('pages.forum_post_create.error_message'), 'error');
      }
    }
  };

  return (
    <Page className={buildClass()} title={t('pages.forum.documentTitle')}>
      {!nickname && popup.isOpen && (
        <Popup
          {...popup.popupProps}
          title={t('pages.forum_nickname.title')}
          showCloseButton={false}
        >
          <PopupCreateNickname
            onNicknameCreated={() => {
              popup.close();
            }}
          />
        </Popup>
      )}
      <div className="col-lg-8 py-8 py-lg-12">
        <Main className={buildClass('content')}>
          <div className={buildClass('introduction')}>
            <h1 className={buildClass('title')}>
              {t('pages.forum_post_create.title')}
            </h1>
          </div>

          <ForumPostForm
            initialValues={{ title: '', body: '' }}
            onSubmit={handleSubmit}
          />
        </Main>
      </div>
    </Page>
  );
};

export default PageForumCreatePost;
